/**
 * Component-model drill-down affordance on the Components list.
 *
 * Renders the resolved `assetComponentModelName` as an underlined
 * `<button>` so Tab reaches it and Enter/Space activate the drill-down.
 * Click opens the Story 062 Component Model detail modal in-place over
 * the Components list — the page mounts `<EditComponentModelDialog>` at
 * its root and keys it on the `?model=<uuid>` URL param.
 *
 * Visual mirrors `TurbineLinkCell` for consistency.
 */
import { Box } from "@mui/material";
import type { MouseEvent, ReactElement } from "react";

export interface ComponentModelLinkCellProps {
  name: string;
  onOpen: () => void;
}

export function ComponentModelLinkCell({
  name,
  onOpen,
}: ComponentModelLinkCellProps): ReactElement {
  function handleClick(e: MouseEvent<HTMLButtonElement>): void {
    e.stopPropagation();
    onOpen();
  }
  return (
    <Box
      component="button"
      type="button"
      onClick={handleClick}
      // The underline colours read from the JS palette, not from
      // `var(--mui-palette-*)`. This theme is built with plain `createTheme()`
      // rather than `extendTheme()`, so MUI emits no CSS variables at all and
      // those references resolved to `currentColor` — an underline the same
      // colour as the text it sits under, i.e. no visible underline. Same
      // defect and same fix as `ParkCell` (SB6-G2 Bug-7).
      sx={(theme) => ({
        background: "none",
        border: "none",
        padding: 0,
        color: "text.primary",
        cursor: "pointer",
        textAlign: "inherit",
        fontFamily: "inherit",
        fontSize: "inherit",
        textDecoration: "underline",
        textUnderlineOffset: "3px",
        textDecorationColor: theme.palette.text.secondary,
        "&:hover, &:focus-visible": {
          textDecorationColor: theme.palette.primary.main,
        },
        "&:focus-visible": {
          outline: "2px solid",
          outlineColor: "primary.main",
          outlineOffset: "2px",
          borderRadius: "2px",
        },
      })}
    >
      {name}
    </Box>
  );
}
